import { apiEndpoint } from '../config/api';
import { apiFetch } from './api';

async function parseResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `Request failed with status ${response.status}`);
  }
  return body;
}

// ---- Coach Check-In Audit: Identity Review (season-manager doc 107, slice 1) --------------------
//
// The grain everywhere in this module is ONE FRANCHISE-BOUNDED PROVISIONAL COACH IDENTITY,
// (com_code, typed_name_norm) -- never one evaluation. A single decision governs every current and
// future evaluation arriving under that identity, which is why the queue is tractable at all.

// GET /checkin-identity-review
// params: com_code, search (both optional). Returns
// { rows, candidates, audit_built_at, truncated, max_rows }.
//
// `candidates` is a FLAT list carrying com_code + typed_name_norm; the caller groups it. Nesting it
// server-side would mean a JSON round-trip or an N+1 read for no gain -- the whole candidate set is
// small because it is franchise-scoped by construction.
//
// This allowlist is EXHAUSTIVE and silent: a param missing from it is dropped without error, so the
// caller believes it filtered and receives an unfiltered page. Any new server-side filter must be
// added HERE as well as in the endpoint and the caller.
export async function getCoachIdentityReview(params = {}) {
  const qs = new URLSearchParams();
  if (params.com_code != null) qs.set('com_code', params.com_code);
  if (params.search != null) qs.set('search', params.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await apiFetch(`${apiEndpoint}/checkin-identity-review${suffix}`);
  return parseResponse(response);
}

// POST /checkin-identity-decision
// payload: { com_code, typed_name_norm, decision: 'link' | 'keep_provisional',
//            canonical_employee_id?, reviewer_note? }
//
// 🔴 The payload deliberately carries NO reviewer identity, NO coach display name and NO franchise
// snapshot. reviewed_by comes from the auth token, and both employee snapshots are read server-side
// from the coach dimension -- a client-supplied snapshot could assert a franchise the coach never
// belonged to, which is exactly what the warehouse contract test relies on being true.
//
// The decision is authoritative in the app table IMMEDIATELY; CP-06/CP-08 reflect it only after the
// next dbt build, which the returned row reports as pending_rebuild.
export async function saveCoachIdentityDecision(payload) {
  const response = await apiFetch(`${apiEndpoint}/checkin-identity-decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// POST /checkin-identity-decision-remove   payload: { com_code, typed_name_norm }
// Soft-deactivates the active decision. The identity reverts to the deterministic computed path on
// the next build, so every decision is reversible rather than merely editable. Idempotent.
export async function removeCoachIdentityDecision(payload) {
  const response = await apiFetch(`${apiEndpoint}/checkin-identity-decision-remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// ---- evaluation-level class reconciliation (slice 2a) ------------------------------------------
//
// 🔴 A DIFFERENT GRAIN FROM EVERYTHING ABOVE. The four calls above settle a PERSON across every
// evaluation under (com_code, typed_name_norm). These settle WHICH DASH SESSION one single
// evaluation describes, keyed on checkin_id. Separate table, separate endpoints, separate buttons —
// saving one must never restate the other.
//
// Since slice 2b the warehouse reads the saved coach judgment at its next build, so it reaches
// CP-06 and CP-08 then, not instantly. Every response carries `reporting_integration_pending` so
// the UI label cannot drift from what the warehouse is actually doing.

// GET /checkin-identity-evaluations?com_code=&typed_name_norm=
// The evaluations under one identity, each with any saved reconciliation. Fetched lazily on expand.
// Also returns `facilities`: every facility of that franchise, for the candidate panel's selector.
// The server derives that franchise from the returned rows, so the list cannot describe a different
// franchise from the evaluations beside it.
export async function getIdentityEvaluations(params = {}) {
  const qs = new URLSearchParams();
  if (params.com_code != null) qs.set('com_code', params.com_code);
  if (params.typed_name_norm != null) qs.set('typed_name_norm', params.typed_name_norm);
  const response = await apiFetch(`${apiEndpoint}/checkin-identity-evaluations?${qs.toString()}`);
  return parseResponse(response);
}

// GET /checkin-occurrence-candidates?checkin_id=&relax_time=&date=&facility_id=&relax_program=
// Candidate DATED sessions for one evaluation, plus each candidate's configured coaches.
//
// The franchise is NOT a parameter: the server derives it from the checkin_id and ignores anything
// a client sends, so no search can cross a franchise. Everything else may be broadened, because the
// Form's facility, date and time are all sometimes wrong and an auditor who cannot search past them
// cannot correct them. This allowlist is exhaustive and silent — a param missing from it is dropped.
export async function getOccurrenceCandidates(params = {}) {
  const qs = new URLSearchParams();
  if (params.checkin_id != null) qs.set('checkin_id', params.checkin_id);
  if (params.relax_time) qs.set('relax_time', '1');
  if (params.relax_program) qs.set('relax_program', '1');
  if (params.date != null) qs.set('date', params.date);
  if (params.facility_id !== undefined) qs.set('facility_id', params.facility_id ?? '');
  const response = await apiFetch(`${apiEndpoint}/checkin-occurrence-candidates?${qs.toString()}`);
  return parseResponse(response);
}

// GET /checkin-coach-search?checkin_id=&search=
// Coaches of this evaluation's franchise whose name matches, for picking a coach who is NOT on the
// selected class's configured roster. Substitutes are real and Dash rosters go stale, so the roster
// is a starting point rather than the allowed set.
//
// The franchise is NOT a parameter: the server derives it from the checkin_id. The result is capped
// (`max_rows`, with `truncated`) because the largest franchise has ~2,450 coaches, and a term
// shorter than `min_search_length` returns nothing rather than most of the roster.
export async function searchFranchiseCoaches(params = {}) {
  const qs = new URLSearchParams();
  if (params.checkin_id != null) qs.set('checkin_id', params.checkin_id);
  if (params.search != null) qs.set('search', params.search);
  const response = await apiFetch(`${apiEndpoint}/checkin-coach-search?${qs.toString()}`);
  return parseResponse(response);
}

// POST /checkin-evaluation-resolution
// payload: { checkin_id, scheduled_occurrence_id, scheduled_team_id,
//            selected_employee_id?, reviewer_note? }
// Applies to ONE evaluation. Snapshots and reviewer are captured server-side.
export async function saveEvaluationResolution(payload) {
  const response = await apiFetch(`${apiEndpoint}/checkin-evaluation-resolution`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// POST /checkin-evaluation-resolution-remove   payload: { checkin_id }
// Soft-deactivates this evaluation's reconciliation. Never touches the identity-wide decision.
export async function removeEvaluationResolution(payload) {
  const response = await apiFetch(`${apiEndpoint}/checkin-evaluation-resolution-remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}
